import React, { useState, useEffect } from 'react';
import {
  fetchCateringPayments,
  fetchRestaurantPayments,
  normalizeRestaurantPaymentsSummary,
} from './orderApi';
import './css/Payment.css';

const getEffectiveRestaurantId = () => {
  const stored = localStorage.getItem('restaurantId');
  if (stored) return stored;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.restaurantId != null) return String(user.restaurantId);
  } catch {
    // ignore
  }
  return '';
};

const Payment = () => {
  const [payments, setPayments] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState('online');
  const [dateFilter, setDateFilter] = useState(''); // Date filter: empty = all dates, YYYY-MM-DD = that date only
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all' | 'week' | 'month'

  useEffect(() => {
    fetchPaymentsData();
  }, [orderTypeFilter]);

  const handleOrderTypeFilterChange = (event) => {
    setOrderTypeFilter(event.target.value);
  };

  // Normalize payment date to YYYY-MM-DD for comparison
  const getPaymentDateOnly = (p) => {
    const d = (p.date || '').toString().trim();
    return d.includes('T') ? d.split('T')[0] : d.substring(0, 10);
  };

  // Current week (Monday–Sunday) start and end in YYYY-MM-DD
  const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
    const monday = new Date(now.getFullYear(), now.getMonth(), diff);
    const mondayStr = monday.toISOString().slice(0, 10);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    return { start: mondayStr, end: sunday.toISOString().slice(0, 10) };
  };

  // Current month first and last day in YYYY-MM-DD
  const getCurrentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return { start: `${y}-${m}-01`, end: new Date(y, now.getMonth() + 1, 0).toISOString().slice(0, 10) };
  };

  // Payments filtered by period: single date (from picker), all, this week, or this month
  const filteredPaymentsByDate = (() => {
    if (dateFilter) {
      return payments.filter((p) => getPaymentDateOnly(p) === dateFilter);
    }
    if (periodFilter === 'all') return payments;
    if (periodFilter === 'week') {
      const { start, end } = getCurrentWeekRange();
      return payments.filter((p) => {
        const d = getPaymentDateOnly(p);
        return d >= start && d <= end;
      });
    }
    if (periodFilter === 'month') {
      const { start, end } = getCurrentMonthRange();
      return payments.filter((p) => {
        const d = getPaymentDateOnly(p);
        return d >= start && d <= end;
      });
    }
    return payments;
  })();

  const fetchPaymentsData = async () => {
    try {
      const effectiveRestaurantId = getEffectiveRestaurantId();
      if (!effectiveRestaurantId) {
        setPayments([]);
        return;
      }

      const data =
        orderTypeFilter === 'catering'
          ? await fetchCateringPayments(effectiveRestaurantId)
          : await fetchRestaurantPayments(effectiveRestaurantId);

      setPayments(normalizeRestaurantPaymentsSummary(data));
    } catch (error) {
      console.error('Error fetching payment data:', error?.response?.data || error);
      setPayments([]);
    }
  };

  return (
    <div className="payment-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="order-type-filter">Order Type:</label>
          <select
            id="order-type-filter"
            value={orderTypeFilter}
            onChange={handleOrderTypeFilterChange}
            style={{ 
              width: '180px', 
              padding: '8px 12px', 
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              margin: 0,
              height: 'auto',
              lineHeight: 'normal',
              boxSizing: 'border-box'
            }}
          >
            <option value="online">Restaurant</option>
            <option value="catering">Catering</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <input
            id="payment-date-filter"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value || '')}
            style={{ 
              padding: '8px 12px', 
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              margin: 0,
              height: 'auto',
              lineHeight: 'normal',
              boxSizing: 'border-box'
            }}
          />
          <select
            id="payment-period-filter"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            style={{ 
              padding: '8px 12px', 
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer',
              margin: 0,
              height: 'auto',
              lineHeight: 'normal',
              boxSizing: 'border-box',
              minWidth: '120px'
            }}
          >
            <option value="all">All</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

      </div>

      <div>
          <table className="payment-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Total No. of Orders</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredPaymentsByDate.length > 0 ? (
                filteredPaymentsByDate.map((payment, index) => {
                  // Format date to YYYY-MM-DD format
                  let formattedDate = payment.date;
                  try {
                    if (payment.date && payment.date.includes('T')) {
                      formattedDate = payment.date.split('T')[0];
                    } else if (payment.date) {
                      const dateObj = new Date(payment.date);
                      if (!isNaN(dateObj.getTime())) {
                        const year = dateObj.getFullYear();
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        formattedDate = `${year}-${month}-${day}`;
                      }
                    }
                  } catch (error) {
                    console.error('Error formatting date:', error);
                  }
                  
                  return (
                  <tr key={index}>
                      <td>{formattedDate}</td>
                    <td>{payment.totalOrders}</td>
                    <td>{payment.totalAmount}</td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3">
                    {dateFilter
                      ? `No payments for ${dateFilter}`
                      : periodFilter === 'week'
                        ? 'No payments for this week'
                        : periodFilter === 'month'
                          ? 'No payments for this month'
                          : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>
    </div>
  );
};

export default Payment;
